import React from 'react';
import { useRownd } from '@rownd/react';
import { Typography, Container, Grid, Card, CardContent, Button, Box, Divider, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { Check, Star } from '@mui/icons-material';

const PricingPage: React.FC = () => {
  const { requestSignIn, is_authenticated } = useRownd();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-200 pb-12">
      <Box className="py-16 bg-gradient-to-r from-purple-700 to-blue-500 text-white mb-8">
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" className="font-bold mb-4 text-center">
            VIBE Coding Pricing
          </Typography>
          <Typography variant="h6" className="text-center">
            Simple, transparent pricing - just keep vibing
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Box className="text-center mb-12">
          <Typography variant="h4" component="h2" className="mb-4 font-bold text-gray-800">
            As long as you're vibing, you pay just $1/month
          </Typography>
          <Typography variant="body1" className="mb-6 text-gray-700 max-w-2xl mx-auto">
            We believe that when you're in the flow state of VIBE Coding, creating amazing applications should be 
            affordable. Our revolutionary pricing model keeps costs low while you're actively vibing.
          </Typography>
        </Box>

        <Grid container spacing={4} className="mb-16">
          <Grid item xs={12} md={4}>
            <Card className="h-full shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-purple-500 text-white px-4 py-1 rounded-bl-lg font-bold">
                Basic
              </div>
              <CardContent className="pt-12">
                <Typography variant="h3" component="h3" className="font-bold mb-2 text-center text-gray-800">
                  $1
                </Typography>
                <Typography variant="subtitle1" className="mb-6 text-center text-gray-500">
                  per month while vibing
                </Typography>

                <Divider className="my-6" />

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="Unlimited VIBE Coding sessions" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="Basic Rownd authentication" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="Community support" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="1 VIBE project" />
                  </ListItem>
                </List>

                <Box className="mt-8 text-center">
                  <Button 
                    variant="contained" 
                    color="primary" 
                    size="large"
                    fullWidth
                    onClick={() => requestSignIn()}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {is_authenticated ? 'Access Dashboard' : 'Start Vibing Now'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className="h-full shadow-xl relative overflow-hidden border-2 border-purple-500 transform scale-105">
              <div className="absolute top-0 right-0 bg-purple-600 text-white px-4 py-1 rounded-bl-lg font-bold">
                Pro
              </div>
              <div className="absolute top-5 left-5">
                <Star className="text-yellow-400 text-xl" />
              </div>
              <CardContent className="pt-12">
                <Typography variant="h3" component="h3" className="font-bold mb-2 text-center text-gray-800">
                  $5
                </Typography>
                <Typography variant="subtitle1" className="mb-6 text-center text-gray-500">
                  per month while vibing
                </Typography>

                <Divider className="my-6" />

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="Everything in Basic" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="Advanced Rownd authentication features" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="Priority support" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="5 VIBE projects" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="Custom domain support" />
                  </ListItem>
                </List>

                <Box className="mt-8 text-center">
                  <Button 
                    variant="contained" 
                    color="primary" 
                    size="large"
                    fullWidth
                    onClick={() => requestSignIn()}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {is_authenticated ? 'Upgrade Now' : 'Get Started'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className="h-full shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-purple-500 text-white px-4 py-1 rounded-bl-lg font-bold">
                Enterprise
              </div>
              <CardContent className="pt-12">
                <Typography variant="h3" component="h3" className="font-bold mb-2 text-center text-gray-800">
                  Custom
                </Typography>
                <Typography variant="subtitle1" className="mb-6 text-center text-gray-500">
                  contact for pricing
                </Typography>

                <Divider className="my-6" />

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="Everything in Pro" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="Dedicated account manager" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="SLA guarantees" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="Unlimited VIBE projects" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Check className="text-green-500" />
                    </ListItemIcon>
                    <ListItemText primary="Custom integrations" />
                  </ListItem>
                </List>

                <Box className="mt-8 text-center">
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    size="large"
                    fullWidth
                    onClick={() => requestSignIn()}
                    className="border-purple-600 text-purple-600 hover:bg-purple-50"
                  >
                    Contact Sales
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* FAQ Section */}
        <Box className="mb-16">
          <Typography variant="h4" component="h2" className="mb-8 font-bold text-gray-800 text-center">
            Frequently Asked Questions
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card className="h-full shadow">
                <CardContent>
                  <Typography variant="h6" component="h3" className="font-bold mb-2 text-gray-800">
                    What does "while vibing" mean?
                  </Typography>
                  <Typography variant="body1" className="text-gray-700">
                    "While vibing" means you're actively using our platform to create projects with VIBE Coding. 
                    We only charge you for months where you log in and create or modify your projects. If you take a break, 
                    you won't be charged until you return and start vibing again.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card className="h-full shadow">
                <CardContent>
                  <Typography variant="h6" component="h3" className="font-bold mb-2 text-gray-800">
                    How is the Rownd authentication billed?
                  </Typography>
                  <Typography variant="body1" className="text-gray-700">
                    Rownd authentication is included in your VIBE Coding subscription. We've partnered with Rownd to 
                    provide you with secure, reliable authentication at no additional cost. You'll get access to features 
                    like passwordless sign-in, user management, and more.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card className="h-full shadow">
                <CardContent>
                  <Typography variant="h6" component="h3" className="font-bold mb-2 text-gray-800">
                    Can I switch between plans?
                  </Typography>
                  <Typography variant="body1" className="text-gray-700">
                    Yes! You can upgrade or downgrade your plan at any time. If you upgrade, you'll get immediate access 
                    to all the features of your new plan. If you downgrade, the changes will take effect at the start of 
                    your next billing cycle.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card className="h-full shadow">
                <CardContent>
                  <Typography variant="h6" component="h3" className="font-bold mb-2 text-gray-800">
                    Is there a free trial?
                  </Typography>
                  <Typography variant="body1" className="text-gray-700">
                    Yes! We offer a 14-day free trial of our Pro plan for all new users. No credit card required to get 
                    started. After your trial ends, you can choose to continue with a paid plan or downgrade to our Basic 
                    plan at $1/month while vibing.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box className="bg-gradient-to-r from-purple-700 to-blue-500 text-white p-8 rounded-lg text-center">
          <Typography variant="h4" component="h2" className="mb-4 font-bold">
            Ready to Start VIBE Coding?
          </Typography>
          <Typography variant="body1" className="mb-6 max-w-2xl mx-auto">
            Join thousands of developers who are already experiencing the future of coding with AI-assisted development 
            and secure Rownd authentication.
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            onClick={() => requestSignIn()}
            className="bg-white text-purple-700 hover:bg-gray-100"
          >
            Get Started Now
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default PricingPage; 